import type { MedplumClient } from "@medplum/core";
import {
  CalClient,
  type CalEventType,
  type CalEventTypeHost,
  type CalTeamClient,
} from "@/lib/cal/client";
import { createLogger } from "@/lib/logger";
import { PracticeAppointmentType } from "@/models/practice/PracticeAppointmentType";
import { Provider } from "@/models/provider/Provider";

const logger = createLogger("sync-practice-appointment-type-child-event-mappings");
const MAX_MANAGED_HOST_LOOKUP_CONCURRENCY = 5;
const MAX_APPOINTMENT_TYPE_ANALYSIS_CONCURRENCY = 5;

export interface CalChildEventTypeMapping {
  calUserId: number;
  eventTypeId: number;
}

interface CalManagedHost {
  username: string;
  calUserId?: number;
}

interface UnresolvedManagedHost {
  calUserId?: number;
  reason: string;
}

interface ExtractManagedHostsResult {
  managedHosts: CalManagedHost[];
  unresolvedHosts: UnresolvedManagedHost[];
}

interface CalUsernameCandidate {
  providerId: string;
  calUsername: string;
  hasCurrentRole: boolean;
  isActiveProvider: boolean;
}

export interface SyncPracticeAppointmentTypeChildEventMappingsInput {
  practiceId: string;
  teamId: number;
  /**
   * Optional appointment type scope for targeted syncs (e.g. after editing one event type).
   */
  appointmentTypeId?: string;
  /**
   * Optional reason used for convergence checks on membership churn.
   */
  reason?: "provider-added" | "provider-removed" | "appointment-type-updated";
  /**
   * Optional Cal user ID expected to be added/removed from managed child mappings.
   */
  expectedCalUserId?: number;
}

export interface SyncPracticeAppointmentTypeChildEventMappingsResult {
  scannedCount: number;
  updatedCount: number;
  unchangedCount: number;
  skippedCount: number;
}

/**
 * Describes an apply failure that still carries partial progress for reporting.
 */
export interface ApplyPracticeAppointmentTypeChildEventMappingsVerificationError extends Error {
  appointmentTypeId: string;
  partialResult: SyncPracticeAppointmentTypeChildEventMappingsResult;
}

export interface ChildMappingVerificationIssue {
  code:
    | "parent-metadata-missing"
    | "host-metadata-missing"
    | "per-user-lookup-missing"
    | "per-user-lookup-ambiguous"
    | "per-user-missing-cal-user-id"
    | "pending-propagation"
    | "invalid-proposed-mapping";
  message: string;
  isBlocking: boolean;
  username?: string;
}

export interface PracticeAppointmentTypeChildEventMappingVerification {
  appointmentTypeId: string;
  parentEventTypeId?: number;
  parentEventSlug?: string;
  isManagedParent: boolean;
  currentMappings: CalChildEventTypeMapping[];
  proposedMappings: CalChildEventTypeMapping[];
  teamChildMappings: CalChildEventTypeMapping[];
  perUserMappings: CalChildEventTypeMapping[];
  managedHosts: Array<{ username: string; calUserId?: number }>;
  issues: ChildMappingVerificationIssue[];
  action: "update" | "unchanged" | "skipped" | "deferred";
}

export interface VerifyPracticeAppointmentTypeChildEventMappingsResult {
  scannedCount: number;
  wouldUpdateCount: number;
  unchangedCount: number;
  skippedCount: number;
  deferredCount: number;
  verificationFailureCount: number;
  pendingPropagationCount: number;
  appointmentTypes: PracticeAppointmentTypeChildEventMappingVerification[];
}

interface ManagedChildCandidateResolution {
  status: "resolved" | "not-found" | "ambiguous";
  eventType?: CalEventType;
  reason: string;
}

interface ManagedHostResolution {
  host: CalManagedHost;
  status: "resolved" | "not-found" | "ambiguous" | "missing-cal-user-id";
  mapping?: CalChildEventTypeMapping;
  reason: string;
}

/**
 * Normalizes mappings so comparisons stay stable across API retries.
 */
function toCanonicalMappings(
  mappings: CalChildEventTypeMapping[] | undefined,
): CalChildEventTypeMapping[] {
  if (!mappings || mappings.length === 0) {
    return [];
  }

  const uniqueByCalUserId = new Map<number, number>();
  for (const mapping of mappings) {
    uniqueByCalUserId.set(mapping.calUserId, mapping.eventTypeId);
  }

  return Array.from(uniqueByCalUserId.entries())
    .map(([calUserId, eventTypeId]) => ({ calUserId, eventTypeId }))
    .sort((left, right) => left.calUserId - right.calUserId);
}

/**
 * Trims usernames so host lookups use a stable key.
 */
function normalizeCalUsername(username: string | null | undefined): string | undefined {
  const normalized = username?.trim();
  return normalized && normalized.length > 0 ? normalized : undefined;
}

/**
 * Resolves the owning Cal user ID across the payload shapes Cal returns.
 */
function getEventTypeCalUserId(eventType: CalEventType): number | undefined {
  if (typeof eventType.ownerId === "number") {
    return eventType.ownerId;
  }

  if (typeof eventType.userId === "number") {
    return eventType.userId;
  }

  if (eventType.users?.length === 1 && typeof eventType.users[0]?.id === "number") {
    return eventType.users[0].id;
  }

  return undefined;
}

/**
 * Indexes mappings by Cal user for quick host fallbacks.
 */
function buildMappingsByCalUserId(
  mappings: CalChildEventTypeMapping[],
): Map<number, CalChildEventTypeMapping> {
  return new Map(mappings.map((mapping) => [mapping.calUserId, mapping]));
}

/**
 * Rebuilds fallback mappings only for the unresolved hosts we can still identify safely.
 */
function buildFallbackMappingsForUnresolvedHosts({
  unresolvedHosts,
  teamChildMappingsByCalUserId,
}: {
  unresolvedHosts: UnresolvedManagedHost[];
  teamChildMappingsByCalUserId: Map<number, CalChildEventTypeMapping>;
}): {
  fallbackMappings: CalChildEventTypeMapping[];
  hasCoverageForAllUnresolvedHosts: boolean;
} {
  if (unresolvedHosts.length === 0) {
    return {
      fallbackMappings: [],
      hasCoverageForAllUnresolvedHosts: false,
    };
  }

  const hasCoverageForAllUnresolvedHosts = unresolvedHosts.every((host) => {
    return typeof host.calUserId === "number" && teamChildMappingsByCalUserId.has(host.calUserId);
  });

  if (!hasCoverageForAllUnresolvedHosts) {
    return {
      fallbackMappings: [],
      hasCoverageForAllUnresolvedHosts,
    };
  }

  const fallbackMappings = toCanonicalMappings(
    unresolvedHosts
      .map((host) => {
        return typeof host.calUserId === "number"
          ? teamChildMappingsByCalUserId.get(host.calUserId)
          : undefined;
      })
      .filter((mapping): mapping is CalChildEventTypeMapping => mapping !== undefined),
  );

  return {
    fallbackMappings,
    hasCoverageForAllUnresolvedHosts,
  };
}

/**
 * Returns whether one mapping set already references the Cal user.
 */
function doesMappingsIncludeCalUserId({
  mappings,
  calUserId,
}: {
  mappings: CalChildEventTypeMapping[];
  calUserId: number;
}): boolean {
  return mappings.some((mapping) => mapping.calUserId === calUserId);
}

/**
 * Returns whether managed host metadata already identifies the Cal user.
 */
function doesManagedHostsIncludeCalUserId({
  managedHosts,
  calUserId,
}: {
  managedHosts: CalManagedHost[];
  calUserId: number;
}): boolean {
  return managedHosts.some((host) => host.calUserId === calUserId);
}

/**
 * Returns whether membership churn should block on this appointment type.
 */
function isExpectedCalUserRelevantToAppointmentType({
  currentMappings,
  expectedCalUserId,
  managedHosts,
  parentEventType,
  perUserMappings,
  proposedMappings,
  reason,
  teamChildMappings,
}: {
  currentMappings: CalChildEventTypeMapping[];
  expectedCalUserId?: number;
  managedHosts: CalManagedHost[];
  parentEventType?: CalEventType;
  perUserMappings: CalChildEventTypeMapping[];
  proposedMappings: CalChildEventTypeMapping[];
  reason?: SyncPracticeAppointmentTypeChildEventMappingsInput["reason"];
  teamChildMappings: CalChildEventTypeMapping[];
}): boolean {
  if (typeof expectedCalUserId !== "number") {
    return false;
  }

  const hasExpectedUserInMappings = [
    currentMappings,
    teamChildMappings,
    perUserMappings,
    proposedMappings,
  ].some((mappings) => {
    return doesMappingsIncludeCalUserId({
      mappings,
      calUserId: expectedCalUserId,
    });
  });
  if (hasExpectedUserInMappings) {
    return true;
  }

  if (
    doesManagedHostsIncludeCalUserId({
      managedHosts,
      calUserId: expectedCalUserId,
    })
  ) {
    return true;
  }

  return reason === "provider-added" && parentEventType?.assignAllTeamMembers === true;
}

/**
 * Runs async work in small batches so practice-wide syncs do not spike Cal traffic.
 */
async function mapWithConcurrencyLimit<TItem, TResult>({
  items,
  limit,
  mapItem,
}: {
  items: TItem[];
  limit: number;
  mapItem: (item: TItem, index: number) => Promise<TResult>;
}): Promise<TResult[]> {
  if (items.length === 0) {
    return [];
  }

  const results: TResult[] = [];

  for (let startIndex = 0; startIndex < items.length; startIndex += limit) {
    const batch = items.slice(startIndex, startIndex + limit);
    const batchResults = await Promise.all(
      batch.map(async (item, batchIndex) => {
        return mapItem(item, startIndex + batchIndex);
      }),
    );

    results.push(...batchResults);
  }

  return results;
}

/**
 * Resolves the Cal user ID from the host shapes Cal returns.
 */
export function getManagedHostCalUserId(host: CalEventTypeHost): number | undefined {
  if (typeof host.userId === "number") {
    return host.userId;
  }

  if (typeof host.user?.id === "number") {
    return host.user.id;
  }

  if (typeof host.id === "number") {
    return host.id;
  }

  return undefined;
}

/**
 * Ranks provider-backed username candidates so current practice records win.
 */
function getCalUsernameCandidateRank(candidate: CalUsernameCandidate): number {
  if (candidate.hasCurrentRole && candidate.isActiveProvider) {
    return 3;
  }

  if (candidate.hasCurrentRole) {
    return 2;
  }

  if (candidate.isActiveProvider) {
    return 1;
  }

  return 0;
}

/**
 * Prefers active/current providers when duplicate Cal IDs disagree on username.
 */
function resolveCalUsernameForCandidates({
  calUserId,
  candidates,
  practiceId,
}: {
  calUserId: number;
  candidates: CalUsernameCandidate[];
  practiceId: string;
}): string | undefined {
  const usernames = Array.from(new Set(candidates.map((candidate) => candidate.calUsername)));
  if (usernames.length === 1) {
    return usernames[0];
  }

  const highestRank = Math.max(...candidates.map(getCalUsernameCandidateRank));
  if (highestRank > 0) {
    const preferredCandidates = candidates.filter((candidate) => {
      return getCalUsernameCandidateRank(candidate) === highestRank;
    });
    const preferredUsernames = Array.from(
      new Set(preferredCandidates.map((candidate) => candidate.calUsername)),
    );

    if (preferredUsernames.length === 1) {
      return preferredUsernames[0];
    }
  }

  logger.warn("Conflicting provider usernames found for Cal user; leaving username unresolved", {
    practiceId,
    calUserId,
    candidates: candidates.map((candidate) => ({
      providerId: candidate.providerId,
      calUsername: candidate.calUsername,
      hasCurrentRole: candidate.hasCurrentRole,
      isActiveProvider: candidate.isActiveProvider,
    })),
  });

  return undefined;
}

/**
 * Builds a Cal user -> username index from practice providers.
 */
async function loadCalUsernameByUserIdForPractice({
  medplum,
  practiceId,
}: {
  medplum: MedplumClient;
  practiceId: string;
}): Promise<Map<number, string>> {
  const providers = await Provider.searchAll(
    medplum,
    {
      practiceRef: `Organization/${practiceId}`,
      includeInactive: true,
    },
    true,
  );
  const calUsernameByUserId = new Map<number, string>();
  const usernameCandidatesByUserId = new Map<number, CalUsernameCandidate[]>();

  for (const provider of providers) {
    const calUsername = normalizeCalUsername(provider.calUsername);
    if (!calUsername) {
      continue;
    }

    let calUserId: number | undefined;
    try {
      const providerCalUserId = provider.calUserId;
      calUserId = typeof providerCalUserId === "number" ? providerCalUserId : undefined;
    } catch (error) {
      logger.warn("Provider has malformed Cal user ID while indexing managed hosts", {
        practiceId,
        providerId: provider.id,
        error,
      });
      continue;
    }

    if (typeof calUserId !== "number") {
      continue;
    }

    const existingCandidates = usernameCandidatesByUserId.get(calUserId) ?? [];
    existingCandidates.push({
      providerId: provider.id,
      calUsername,
      hasCurrentRole: provider.providerRole?.isActive ?? false,
      isActiveProvider: provider.active,
    });
    usernameCandidatesByUserId.set(calUserId, existingCandidates);
  }

  for (const [calUserId, candidates] of usernameCandidatesByUserId.entries()) {
    const resolvedUsername = resolveCalUsernameForCandidates({
      calUserId,
      candidates,
      practiceId,
    });
    if (!resolvedUsername) {
      continue;
    }

    calUsernameByUserId.set(calUserId, resolvedUsername);
  }

  return calUsernameByUserId;
}

/**
 * Reads managed hosts from the parent payload and backfills usernames from provider records.
 */
function extractManagedHosts({
  parentEventType,
  calUsernameByUserId,
}: {
  parentEventType: CalEventType;
  calUsernameByUserId: Map<number, string>;
}): ExtractManagedHostsResult {
  const managedHosts = new Map<string, CalManagedHost>();
  const unresolvedHosts: UnresolvedManagedHost[] = [];

  for (const host of parentEventType.hosts ?? []) {
    if (typeof host === "string") {
      const username = normalizeCalUsername(host);
      if (!username) {
        continue;
      }

      const existingHost = managedHosts.get(username);
      managedHosts.set(username, {
        username,
        calUserId: existingHost?.calUserId,
      });
      continue;
    }

    const calUserId = getManagedHostCalUserId(host);
    const username =
      normalizeCalUsername(host.username) ??
      normalizeCalUsername(host.user?.username) ??
      (typeof calUserId === "number" ? calUsernameByUserId.get(calUserId) : undefined);
    if (!username) {
      unresolvedHosts.push({
        calUserId,
        reason:
          typeof calUserId === "number"
            ? `Managed host did not include a username and no provider mapping exists for Cal user ${calUserId}`
            : "Managed host did not include a username",
      });
      continue;
    }

    const existingHost = managedHosts.get(username);
    managedHosts.set(username, {
      username,
      calUserId: existingHost?.calUserId ?? calUserId,
    });
  }

  return {
    managedHosts: Array.from(managedHosts.values()).sort((left, right) => {
      return left.username.localeCompare(right.username);
    }),
    unresolvedHosts,
  };
}

/**
 * Returns whether Cal explicitly reported that the managed parent has no hosts left.
 */
function isManagedParentExplicitlyHostless(parentEventType: CalEventType): boolean {
  if (parentEventType.hostsCount === 0) {
    return true;
  }

  return Array.isArray(parentEventType.hosts) && parentEventType.hosts.length === 0;
}

/**
 * Reuses explicit child rows when the team endpoint already returned them.
 */
function buildChildMappingsForParentFromTeamEventTypes({
  teamEventTypes,
  parentEventTypeId,
}: {
  teamEventTypes: CalEventType[];
  parentEventTypeId: number;
}): CalChildEventTypeMapping[] {
  const childMappings = teamEventTypes
    .filter((eventType) => eventType.parentEventTypeId === parentEventTypeId)
    .map((eventType) => {
      const calUserId = getEventTypeCalUserId(eventType);
      if (typeof eventType.id !== "number" || typeof calUserId !== "number") {
        return undefined;
      }

      return {
        calUserId,
        eventTypeId: eventType.id,
      };
    })
    .filter((mapping): mapping is CalChildEventTypeMapping => mapping !== undefined);

  return toCanonicalMappings(childMappings);
}

/**
 * Accepts only child rows that can be tied to the host without guessing.
 */
function pickManagedChildEventType({
  eventTypes,
  parentEventTypeId,
  parentEventSlug,
  host,
}: {
  eventTypes: CalEventType[];
  parentEventTypeId: number;
  parentEventSlug: string;
  host: CalManagedHost;
}): ManagedChildCandidateResolution {
  const slugMatches = eventTypes.filter((candidate) => candidate.slug === parentEventSlug);
  const candidates = slugMatches.length > 0 ? slugMatches : eventTypes;
  const nonParentCandidates = candidates.filter((candidate) => candidate.id !== parentEventTypeId);

  if (nonParentCandidates.length === 0) {
    return {
      status: "not-found",
      reason: "Username lookup returned no non-parent event types",
    };
  }

  if (typeof host.calUserId === "number") {
    const ownerMatches = nonParentCandidates.filter((candidate) => {
      return getEventTypeCalUserId(candidate) === host.calUserId;
    });
    const ownerChildMatches = ownerMatches.filter((candidate) => {
      return candidate.parentEventTypeId === parentEventTypeId;
    });

    if (ownerChildMatches.length === 1) {
      return {
        status: "resolved",
        eventType: ownerChildMatches[0],
        reason: "Resolved managed child by owner + parent linkage",
      };
    }

    if (ownerChildMatches.length > 1) {
      return {
        status: "ambiguous",
        reason: "Username lookup returned multiple child rows for the same owner",
      };
    }

    // Live Cal username + eventSlug responses can omit parentEventTypeId while still returning
    // the single canonical managed child row for the host.
    if (ownerMatches.length === 1 && slugMatches.length > 0) {
      return {
        status: "resolved",
        eventType: ownerMatches[0],
        reason: "Resolved managed child by unique owner-scoped slug match",
      };
    }

    if (ownerMatches.length > 1) {
      return {
        status: "ambiguous",
        reason: "Username lookup returned multiple non-parent rows for the same owner",
      };
    }
  }

  const explicitChildMatches = nonParentCandidates.filter((candidate) => {
    return candidate.parentEventTypeId === parentEventTypeId;
  });
  if (explicitChildMatches.length === 1) {
    return {
      status: "resolved",
      eventType: explicitChildMatches[0],
      reason: "Resolved managed child by explicit parent linkage",
    };
  }

  if (explicitChildMatches.length > 1) {
    return {
      status: "ambiguous",
      reason: "Username lookup returned multiple child rows for the same parent",
    };
  }

  return {
    status: "ambiguous",
    reason: "Username lookup returned rows that could not be tied to the host safely",
  };
}

/**
 * Resolves one managed host using the documented username + eventSlug lookup.
 */
async function loadManagedChildMappingForHost({
  calTeamClient,
  host,
  parentEventType,
}: {
  calTeamClient: CalTeamClient;
  host: CalManagedHost;
  parentEventType: CalEventType;
}): Promise<ManagedHostResolution> {
  const response = await calTeamClient.getEventTypes({
    username: host.username,
    eventSlug: parentEventType.slug,
  });
  if (response.status !== "success") {
    throw new Error(`Failed to load Cal event types for username "${host.username}"`);
  }

  const childEventType = pickManagedChildEventType({
    eventTypes: response.data,
    parentEventTypeId: parentEventType.id,
    parentEventSlug: parentEventType.slug,
    host,
  });
  if (childEventType.status !== "resolved" || !childEventType.eventType) {
    return {
      host,
      status: childEventType.status,
      reason: childEventType.reason,
    };
  }

  const calUserId = getEventTypeCalUserId(childEventType.eventType) ?? host.calUserId;
  if (typeof calUserId !== "number" || typeof childEventType.eventType.id !== "number") {
    return {
      host,
      status: "missing-cal-user-id",
      reason: "Resolved child event did not include a usable Cal user ID",
    };
  }

  return {
    host,
    status: "resolved",
    reason: childEventType.reason,
    mapping: {
      calUserId,
      eventTypeId: childEventType.eventType.id,
    },
  };
}

/**
 * Resolves all managed hosts via username-scoped lookups.
 */
async function loadManagedChildMappingsForParent({
  calTeamClient,
  managedHosts,
  parentEventType,
}: {
  calTeamClient: CalTeamClient;
  managedHosts: CalManagedHost[];
  parentEventType: CalEventType;
}): Promise<ManagedHostResolution[]> {
  return mapWithConcurrencyLimit({
    items: managedHosts,
    limit: MAX_MANAGED_HOST_LOOKUP_CONCURRENCY,
    mapItem: async (host) => {
      return loadManagedChildMappingForHost({
        calTeamClient,
        host,
        parentEventType,
      });
    },
  });
}

/**
 * Compares two mapping sets without caring about order.
 */
function areMappingsEqual(
  left: CalChildEventTypeMapping[] | undefined,
  right: CalChildEventTypeMapping[] | undefined,
): boolean {
  const leftCanonical = toCanonicalMappings(left);
  const rightCanonical = toCanonicalMappings(right);

  if (leftCanonical.length !== rightCanonical.length) {
    return false;
  }

  for (let i = 0; i < leftCanonical.length; i += 1) {
    if (
      leftCanonical[i].calUserId !== rightCanonical[i].calUserId ||
      leftCanonical[i].eventTypeId !== rightCanonical[i].eventTypeId
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Normalizes Cal scheduling type casing.
 */
export function isManagedSchedulingType(schedulingType: CalEventType["schedulingType"]): boolean {
  return typeof schedulingType === "string" && schedulingType.toLowerCase() === "managed";
}

/**
 * Returns whether an issue makes the proposed mappings unsafe to apply.
 */
function hasApplyBlockingIssues(issues: ChildMappingVerificationIssue[]): boolean {
  return issues.some((issue) => issue.isBlocking);
}

/**
 * Catches obviously unsafe writes before they hit Medplum.
 */
function validateProposedMappings({
  proposedMappings,
  parentEventTypeId,
}: {
  proposedMappings: CalChildEventTypeMapping[];
  parentEventTypeId: number;
}): ChildMappingVerificationIssue[] {
  const invalidMappings = proposedMappings.filter((mapping) => {
    return mapping.eventTypeId === parentEventTypeId;
  });

  return invalidMappings.map((mapping) => ({
    code: "invalid-proposed-mapping",
    message: `Proposed mapping for Cal user ${mapping.calUserId} points at the parent event type`,
    isBlocking: true,
  }));
}

/**
 * Analyzes one appointment type and returns a safe apply decision plus verification detail.
 */
async function analyzeAppointmentTypeChildMappings({
  appointmentType,
  calTeamClient,
  calUsernameByUserId,
  input,
  teamEventTypes,
}: {
  appointmentType: PracticeAppointmentType;
  calTeamClient: CalTeamClient;
  calUsernameByUserId: Map<number, string>;
  input: SyncPracticeAppointmentTypeChildEventMappingsInput;
  teamEventTypes: CalEventType[];
}): Promise<PracticeAppointmentTypeChildEventMappingVerification> {
  const { expectedCalUserId, practiceId, reason, teamId } = input;
  const parentEventTypeId = appointmentType.calTeamEventTypeId;
  const currentMappings = toCanonicalMappings(appointmentType.calChildEventTypes);

  if (typeof parentEventTypeId !== "number") {
    return {
      appointmentTypeId: appointmentType.id,
      currentMappings,
      proposedMappings: currentMappings,
      teamChildMappings: [],
      perUserMappings: [],
      managedHosts: [],
      issues: [],
      isManagedParent: false,
      action: "skipped",
    };
  }

  const parentEventType = teamEventTypes.find((eventType) => eventType.id === parentEventTypeId);
  const isAppointmentTypeUpdateSync = reason === "appointment-type-updated";
  const shouldCheckExpectedCalUserConvergence =
    typeof expectedCalUserId === "number" &&
    (reason === "provider-added" || reason === "provider-removed");
  const shouldGuardAgainstMetadataPropagation =
    shouldCheckExpectedCalUserConvergence || isAppointmentTypeUpdateSync;

  const issues: ChildMappingVerificationIssue[] = [];
  const missingParentMetadata = !parentEventType || parentEventType.schedulingType == null;
  if (missingParentMetadata) {
    logger.warn("Unable to verify child mappings because parent event metadata is missing", {
      practiceId,
      teamId,
      appointmentTypeId: appointmentType.id,
      parentEventTypeId,
      reason,
      expectedCalUserId,
    });

    issues.push({
      code: "parent-metadata-missing",
      message: "Parent event metadata is missing from the team event types response",
      isBlocking: true,
    });

    if (shouldGuardAgainstMetadataPropagation) {
      issues.push({
        code: "pending-propagation",
        message: "Parent event metadata has not propagated yet",
        isBlocking: true,
      });
    }

    return {
      appointmentTypeId: appointmentType.id,
      parentEventTypeId,
      currentMappings,
      proposedMappings: currentMappings,
      teamChildMappings: [],
      perUserMappings: [],
      managedHosts: [],
      issues,
      isManagedParent: false,
      action:
        shouldGuardAgainstMetadataPropagation || currentMappings.length > 0
          ? "deferred"
          : "skipped",
    };
  }

  const isManagedParent = isManagedSchedulingType(parentEventType.schedulingType);
  let proposedMappings: CalChildEventTypeMapping[] = [];
  let teamChildMappings: CalChildEventTypeMapping[] = [];
  let perUserMappings: CalChildEventTypeMapping[] = [];
  let managedHosts: CalManagedHost[] = [];

  if (isManagedParent) {
    teamChildMappings = buildChildMappingsForParentFromTeamEventTypes({
      teamEventTypes,
      parentEventTypeId,
    });
    const extractedManagedHosts = extractManagedHosts({
      parentEventType,
      calUsernameByUserId,
    });
    managedHosts = extractedManagedHosts.managedHosts;
    const unresolvedHosts = extractedManagedHosts.unresolvedHosts;
    const teamChildMappingsByCalUserId = buildMappingsByCalUserId(teamChildMappings);
    const isParentExplicitlyHostless = isManagedParentExplicitlyHostless(parentEventType);
    const { fallbackMappings, hasCoverageForAllUnresolvedHosts } =
      buildFallbackMappingsForUnresolvedHosts({
        unresolvedHosts,
        teamChildMappingsByCalUserId,
      });

    if (managedHosts.length === 0) {
      const shouldClearMappingsForExplicitEmptyHostSet =
        isParentExplicitlyHostless &&
        unresolvedHosts.length === 0 &&
        teamChildMappings.length === 0;

      if (shouldClearMappingsForExplicitEmptyHostSet) {
        proposedMappings = [];
      } else if (hasCoverageForAllUnresolvedHosts) {
        proposedMappings = fallbackMappings;
      } else {
        logger.warn("Unable to verify managed child mappings because host metadata is missing", {
          practiceId,
          teamId,
          appointmentTypeId: appointmentType.id,
          parentEventTypeId,
          parentEventSlug: parentEventType.slug,
          reason,
          expectedCalUserId,
          unresolvedHostCount: unresolvedHosts.length,
        });

        issues.push({
          code: "host-metadata-missing",
          message:
            unresolvedHosts[0]?.reason ??
            "Managed parent did not include host metadata or explicit child rows",
          isBlocking: true,
        });

        if (shouldGuardAgainstMetadataPropagation) {
          issues.push({
            code: "pending-propagation",
            message: "Managed host metadata has not propagated yet",
            isBlocking: true,
          });
        }
      }
    } else {
      const hostResolutions = await loadManagedChildMappingsForParent({
        calTeamClient,
        managedHosts,
        parentEventType,
      });
      const resolvedMappings: CalChildEventTypeMapping[] = [];

      for (const unresolvedHost of unresolvedHosts) {
        if (typeof unresolvedHost.calUserId === "number") {
          const fallbackMapping = teamChildMappingsByCalUserId.get(unresolvedHost.calUserId);
          if (fallbackMapping) {
            resolvedMappings.push(fallbackMapping);
            continue;
          }
        }

        logger.warn(
          "Unable to resolve managed host username; leaving existing mappings unchanged",
          {
            practiceId,
            teamId,
            appointmentTypeId: appointmentType.id,
            parentEventTypeId,
            parentEventSlug: parentEventType.slug,
            calUserId: unresolvedHost.calUserId,
            reason: unresolvedHost.reason,
          },
        );
        issues.push({
          code: "host-metadata-missing",
          message: unresolvedHost.reason,
          isBlocking: true,
        });
      }

      for (const hostResolution of hostResolutions) {
        if (hostResolution.mapping) {
          resolvedMappings.push(hostResolution.mapping);
          continue;
        }

        if (hostResolution.status === "not-found") {
          logger.warn(
            "Managed username lookup returned no child event type; deferring mapping changes",
            {
              practiceId,
              teamId,
              appointmentTypeId: appointmentType.id,
              parentEventTypeId,
              parentEventSlug: parentEventType.slug,
              username: hostResolution.host.username,
              reason: hostResolution.reason,
            },
          );
          issues.push({
            code: "per-user-lookup-missing",
            message: hostResolution.reason,
            isBlocking: true,
            username: hostResolution.host.username,
          });
        }

        if (hostResolution.status === "ambiguous") {
          logger.warn(
            "Managed username lookup was ambiguous; leaving existing mappings unchanged",
            {
              practiceId,
              teamId,
              appointmentTypeId: appointmentType.id,
              parentEventTypeId,
              parentEventSlug: parentEventType.slug,
              username: hostResolution.host.username,
              reason: hostResolution.reason,
            },
          );
          issues.push({
            code: "per-user-lookup-ambiguous",
            message: hostResolution.reason,
            isBlocking: true,
            username: hostResolution.host.username,
          });
        }

        if (hostResolution.status === "missing-cal-user-id") {
          logger.warn("Resolved managed child event did not expose a Cal user ID", {
            practiceId,
            teamId,
            appointmentTypeId: appointmentType.id,
            parentEventTypeId,
            parentEventSlug: parentEventType.slug,
            username: hostResolution.host.username,
          });
          issues.push({
            code: "per-user-missing-cal-user-id",
            message: hostResolution.reason,
            isBlocking: true,
            username: hostResolution.host.username,
          });
        }
      }

      perUserMappings = toCanonicalMappings(
        hostResolutions
          .map((hostResolution) => hostResolution.mapping)
          .filter((mapping): mapping is CalChildEventTypeMapping => mapping !== undefined),
      );
      proposedMappings = toCanonicalMappings(resolvedMappings);
    }
  }

  issues.push(
    ...validateProposedMappings({
      proposedMappings,
      parentEventTypeId,
    }),
  );

  const isExpectedCalUserRelevant =
    isManagedParent && shouldCheckExpectedCalUserConvergence
      ? isExpectedCalUserRelevantToAppointmentType({
          currentMappings,
          expectedCalUserId,
          managedHosts,
          parentEventType,
          perUserMappings,
          proposedMappings,
          reason,
          teamChildMappings,
        })
      : false;

  if (isManagedParent && shouldCheckExpectedCalUserConvergence && isExpectedCalUserRelevant) {
    const hasExpectedUser = proposedMappings.some((mapping) => {
      return mapping.calUserId === expectedCalUserId;
    });

    if (reason === "provider-added" && !hasExpectedUser) {
      issues.push({
        code: "pending-propagation",
        message: `Expected Cal user ${expectedCalUserId} is not present in the proposed mappings yet`,
        isBlocking: true,
      });
    }

    if (reason === "provider-removed" && hasExpectedUser) {
      issues.push({
        code: "pending-propagation",
        message: `Expected Cal user ${expectedCalUserId} is still present in the proposed mappings`,
        isBlocking: true,
      });
    }
  }

  const hasPendingPropagationIssue = issues.some((issue) => {
    return issue.code === "pending-propagation" && issue.isBlocking;
  });
  if (
    shouldCheckExpectedCalUserConvergence &&
    isExpectedCalUserRelevant &&
    hasApplyBlockingIssues(issues) &&
    !hasPendingPropagationIssue
  ) {
    issues.push({
      code: "pending-propagation",
      message: "Managed child mappings are still converging after the team membership change",
      isBlocking: true,
    });
  }

  if (
    reason === "appointment-type-updated" &&
    hasApplyBlockingIssues(issues) &&
    !hasPendingPropagationIssue
  ) {
    issues.push({
      code: "pending-propagation",
      message: "Managed child mappings are still converging after the appointment type edit",
      isBlocking: true,
    });
  }

  const action = getMappingSyncAction({
    issues,
    shouldGuardAgainstMetadataPropagation,
    currentMappings,
    proposedMappings,
  });

  return {
    appointmentTypeId: appointmentType.id,
    parentEventTypeId,
    parentEventSlug: parentEventType.slug,
    currentMappings,
    proposedMappings,
    teamChildMappings,
    perUserMappings,
    managedHosts,
    issues,
    isManagedParent,
    action,
  };
}

/**
 * Computes a detailed verification report without mutating Medplum.
 */
export async function verifyPracticeAppointmentTypeChildEventMappings(
  medplum: MedplumClient,
  input: SyncPracticeAppointmentTypeChildEventMappingsInput,
): Promise<VerifyPracticeAppointmentTypeChildEventMappingsResult> {
  const { appointmentTypeId, practiceId, teamId } = input;

  const allAppointmentTypes = await PracticeAppointmentType.searchAllByPractice(
    medplum,
    practiceId,
  );
  const appointmentTypes = appointmentTypeId
    ? allAppointmentTypes.filter((appointmentType) => appointmentType.id === appointmentTypeId)
    : allAppointmentTypes;

  if (appointmentTypes.length === 0) {
    return {
      scannedCount: 0,
      wouldUpdateCount: 0,
      unchangedCount: 0,
      skippedCount: 0,
      deferredCount: 0,
      verificationFailureCount: 0,
      pendingPropagationCount: 0,
      appointmentTypes: [],
    };
  }

  const calTeamClient = CalClient.forTeam();
  const teamEventTypesResponse = await calTeamClient.getTeamEventTypes({ teamId });
  if (teamEventTypesResponse.status !== "success") {
    throw new Error("Failed to load team event types for child mapping sync");
  }
  const calUsernameByUserId = await loadCalUsernameByUserIdForPractice({
    medplum,
    practiceId,
  });

  const appointmentTypeVerifications = await mapWithConcurrencyLimit({
    items: appointmentTypes,
    limit: MAX_APPOINTMENT_TYPE_ANALYSIS_CONCURRENCY,
    mapItem: async (appointmentType) => {
      return analyzeAppointmentTypeChildMappings({
        appointmentType,
        calTeamClient,
        calUsernameByUserId,
        input,
        teamEventTypes: teamEventTypesResponse.data,
      });
    },
  });

  return {
    scannedCount: appointmentTypes.length,
    wouldUpdateCount: appointmentTypeVerifications.filter((item) => item.action === "update")
      .length,
    unchangedCount: appointmentTypeVerifications.filter((item) => item.action === "unchanged")
      .length,
    skippedCount: appointmentTypeVerifications.filter((item) => item.action === "skipped").length,
    deferredCount: appointmentTypeVerifications.filter((item) => item.action === "deferred").length,
    verificationFailureCount: appointmentTypeVerifications.reduce((count, item) => {
      return count + item.issues.filter((issue) => hasApplyBlockingIssues([issue])).length;
    }, 0),
    pendingPropagationCount: appointmentTypeVerifications.reduce((count, item) => {
      return (
        count +
        item.issues.filter((issue) => issue.code === "pending-propagation" && issue.isBlocking)
          .length
      );
    }, 0),
    appointmentTypes: appointmentTypeVerifications,
  };
}

/**
 * Wraps apply failures with partial progress so operator scripts can persist a report.
 */
function toApplyPracticeAppointmentTypeChildEventMappingsVerificationError({
  appointmentTypeId,
  error,
  partialResult,
}: {
  appointmentTypeId: string;
  error: unknown;
  partialResult: SyncPracticeAppointmentTypeChildEventMappingsResult;
}): ApplyPracticeAppointmentTypeChildEventMappingsVerificationError {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const applyError = new Error(
    `Failed to apply child mappings for appointment type ${appointmentTypeId}: ${errorMessage}`,
  ) as ApplyPracticeAppointmentTypeChildEventMappingsVerificationError;

  applyError.appointmentTypeId = appointmentTypeId;
  applyError.partialResult = partialResult;

  return applyError;
}

/**
 * Applies the safe updates from a verification pass without recomputing Cal state.
 */
export async function applyPracticeAppointmentTypeChildEventMappingsVerification(
  medplum: MedplumClient,
  verification: VerifyPracticeAppointmentTypeChildEventMappingsResult,
): Promise<SyncPracticeAppointmentTypeChildEventMappingsResult> {
  let updatedCount = 0;
  let unchangedCount = 0;
  let skippedCount = 0;

  for (const appointmentTypeVerification of verification.appointmentTypes) {
    if (appointmentTypeVerification.action === "unchanged") {
      unchangedCount += 1;
      continue;
    }

    if (appointmentTypeVerification.action !== "update") {
      skippedCount += 1;
      continue;
    }

    try {
      await PracticeAppointmentType.update(medplum, appointmentTypeVerification.appointmentTypeId, {
        calChildEventTypes: appointmentTypeVerification.proposedMappings,
      });
      updatedCount += 1;
    } catch (error) {
      throw toApplyPracticeAppointmentTypeChildEventMappingsVerificationError({
        appointmentTypeId: appointmentTypeVerification.appointmentTypeId,
        error,
        partialResult: {
          scannedCount: verification.scannedCount,
          updatedCount,
          unchangedCount,
          skippedCount,
        },
      });
    }
  }

  return {
    scannedCount: verification.scannedCount,
    updatedCount,
    unchangedCount,
    skippedCount,
  };
}

/**
 * Rebuilds and persists Cal managed child event mappings for practice appointment types.
 *
 * We call this after team membership churn (provider add/remove) and event type updates so
 * mobile clients can resolve child event type IDs for slot lookups.
 */
export async function syncPracticeAppointmentTypeChildEventMappings(
  medplum: MedplumClient,
  input: SyncPracticeAppointmentTypeChildEventMappingsInput,
): Promise<SyncPracticeAppointmentTypeChildEventMappingsResult> {
  const verification = await verifyPracticeAppointmentTypeChildEventMappings(medplum, input);
  const result = await applyPracticeAppointmentTypeChildEventMappingsVerification(
    medplum,
    verification,
  );

  if (verification.pendingPropagationCount > 0) {
    if (input.reason === "appointment-type-updated") {
      throw new Error(
        `Managed child mappings not converged yet for appointment-type-updated ` +
          `(pending=${verification.pendingPropagationCount})`,
      );
    }

    if (typeof input.expectedCalUserId === "number") {
      throw new Error(
        `Managed child mappings not converged yet for Cal user ${input.expectedCalUserId} ` +
          `(${input.reason ?? "unknown-reason"}, pending=${verification.pendingPropagationCount})`,
      );
    }
  }

  if (verification.verificationFailureCount > 0) {
    logger.warn("Skipped unsafe child mapping updates after verification", {
      practiceId: input.practiceId,
      teamId: input.teamId,
      appointmentTypeId: input.appointmentTypeId,
      reason: input.reason,
      expectedCalUserId: input.expectedCalUserId,
      verificationFailureCount: verification.verificationFailureCount,
      deferredCount: verification.deferredCount,
      skippedCount: verification.skippedCount,
    });
  }

  logger.info("Synced practice appointment type child event mappings", {
    practiceId: input.practiceId,
    teamId: input.teamId,
    appointmentTypeId: input.appointmentTypeId,
    reason: input.reason,
    expectedCalUserId: input.expectedCalUserId,
    scannedCount: verification.scannedCount,
    updatedCount: result.updatedCount,
    unchangedCount: result.unchangedCount,
    skippedCount: result.skippedCount,
    pendingPropagationCount: verification.pendingPropagationCount,
    verificationFailureCount: verification.verificationFailureCount,
  });

  return result;
}

interface MappingSyncActionInput {
  issues: ChildMappingVerificationIssue[];
  shouldGuardAgainstMetadataPropagation: boolean;
  currentMappings: CalChildEventTypeMapping[];
  proposedMappings: CalChildEventTypeMapping[];
}

/** Blocked syncs defer when there is state to protect and skip otherwise. */
function getMappingSyncAction({
  issues,
  shouldGuardAgainstMetadataPropagation,
  currentMappings,
  proposedMappings,
}: MappingSyncActionInput): PracticeAppointmentTypeChildEventMappingVerification["action"] {
  if (hasApplyBlockingIssues(issues)) {
    return shouldGuardAgainstMetadataPropagation || currentMappings.length > 0
      ? "deferred"
      : "skipped";
  }
  return areMappingsEqual(currentMappings, proposedMappings) ? "unchanged" : "update";
}
